import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { InputCP } from 'submodules/nerit-framework-ui/common/components/form-fields/input/InputCP'
import { SelectCP } from 'submodules/nerit-framework-ui/common/components/form-fields/select/SelectCP'
import { BasicModalCPProps, ModalCP } from 'submodules/nerit-framework-ui/common/components/modal/ModalCP'
import { ButtonCP } from 'submodules/nerit-framework-ui/common/components/button/ButtonCP'
import { TagCP } from 'submodules/nerit-framework-ui/common/components/tag/TagCP'
import { RowCP } from 'submodules/nerit-framework-ui/common/components/grid/row/RowCP'
import { ColumnCP } from 'submodules/nerit-framework-ui/common/components/grid/column/ColumnCP'
import { useRequest } from 'submodules/nerit-framework-ui/common/request-manager/use-request/UseRequest'
import { useFormStateManager } from 'submodules/nerit-framework-ui/common/form-state-manager/UseFormStateManager'
import { FormStateManagerUtils } from 'submodules/nerit-framework-ui/common/form-state-manager/FormStateManagerUtils'
import { RequestUtils } from 'submodules/nerit-framework-utils/sdk-utils/request-manager/RequestUtils'
import { NotificationHelper } from 'submodules/nerit-framework-ui/common/components/notification/inner/NotificationHelper'
import { ListResponseDTO } from 'submodules/nerit-framework-utils/sdk-utils/dtos/response/ListResponseDTO'
import { NameAndCodeResponseDTO } from 'submodules/nerit-framework-utils/sdk-utils/dtos/response/NameAndCodeResponseDTO'
import { MatrixRequests } from 'submodules/nerit-aquatec-sdk/services/matrix/MatrixRequests'
import { MatrixResponseDTO } from 'submodules/nerit-aquatec-sdk/services/matrix/dtos/responses/MatrixResponseDTO'
import { MatrixFormModel } from 'modules/collection-performance/components/matrix/modal-matrix/inner/MatrixFormModel'

interface ICPProps extends BasicModalCPProps {
    matrix?: MatrixResponseDTO
}

/**
 * Modal de criacao/edicao de Matriz (agrupamento nomeado de pontos).
 *
 * - Na criacao os pontos selecionados vao junto com o create.
 * - Na edicao o nome e salvo no OK e a gestao de membros e feita ao vivo
 *   (cada adicao/remocao e uma requisicao propria), pois um ponto so pode
 *   pertencer a uma Matriz e o backend rejeita duplicidade.
 */
export function ModalMatrixCP(props: ICPProps): JSX.Element {

    const isEditing = !!props.matrix

    const [formModel, setFormModel] = useState<MatrixFormModel>(new MatrixFormModel())
    const formStateManager = useFormStateManager(formModel)

    const [selectedLocationCodes, setSelectedLocationCodes] = useState<number[]>([])

    const availableRequest = useRequest<ListResponseDTO<NameAndCodeResponseDTO>>()
    const detailRequest = useRequest<MatrixResponseDTO>()

    const saveNameRequest = useRequest<void>('none')
    useEffect(onSaveNameRequestChange, [saveNameRequest.isAwaiting])

    const addRequest = useRequest<void>('none')
    useEffect(onAddRequestChange, [addRequest.isAwaiting])

    const removeRequest = useRequest<void>('none')
    useEffect(onRemoveRequestChange, [removeRequest.isAwaiting])

    useEffect(init, [props.visible])

    /**
     */
    function init(): void {

        setSelectedLocationCodes([])

        if (!props.visible) {
            setFormModel(new MatrixFormModel())
            return
        }

        setFormModel(new MatrixFormModel({ name: props.matrix?.name }))
        loadData()
    }

    /**
     */
    function loadData(): void {

        availableRequest.runRequest(MatrixRequests.availableLocations({ matrixCode: props.matrix?.code }))

        if (isEditing)
            detailRequest.runRequest(MatrixRequests.get(props.matrix!.code))
    }

    /**
     */
    async function onClickSave(): Promise<void> {

        if (!await FormStateManagerUtils.validateRequiredFields(formStateManager))
            return

        const name = formStateManager.getFormValues()!.name

        if (isEditing)
            saveNameRequest.runRequest(MatrixRequests.update(props.matrix!.code, { name }))
        else
            saveNameRequest.runRequest(MatrixRequests.create({ name, locationCodes: selectedLocationCodes }))
    }

    /**
     */
    function onSaveNameRequestChange(): void {

        if (!RequestUtils.isValidRequestReturn(saveNameRequest, NotificationHelper.DEFAULT_ERROR_SAVE_MESSAGE, NotificationHelper.DEFAULT_SUCCESS_SAVE_MESSAGE, true))
            return

        props.onSave()
    }

    /**
     */
    function onClickAddLocations(): void {

        if (!selectedLocationCodes.length)
            return

        addRequest.runRequest(MatrixRequests.addLocations(props.matrix!.code, { locationCodes: selectedLocationCodes }))
    }

    /**
     */
    function onAddRequestChange(): void {

        if (!RequestUtils.isValidRequestReturn(addRequest, 'Não foi possível adicionar o ponto. Verifique se ele já pertence a outra Matriz.', 'Pontos adicionados!', true))
            return

        setSelectedLocationCodes([])
        loadData()
    }

    /**
     */
    function onClickRemoveLocation(locationCode: number): void {
        removeRequest.runRequest(MatrixRequests.removeLocation(props.matrix!.code, locationCode))
    }

    /**
     */
    function onRemoveRequestChange(): void {

        if (!RequestUtils.isValidRequestReturn(removeRequest, NotificationHelper.DEFAULT_ERROR_DELETE_MESSAGE, NotificationHelper.DEFAULT_SUCCESS_DELETE_MESSAGE, true))
            return

        loadData()
    }

    const members: NameAndCodeResponseDTO[] = detailRequest.responseData?.locations ?? []
    const memberCodes = members.map((member) => member.code)
    const availableLocations = availableRequest.responseData?.list ?? []

    // Na edicao o select de "adicionar" nao deve listar quem ja e membro.
    const selectableLocations = isEditing
        ? availableLocations.filter((location) => !memberCodes.includes(location.code))
        : availableLocations

    return (
        <ModalCP
            visible={props.visible}
            actionLoading={saveNameRequest.isAwaiting}
            onOk={onClickSave}
            onCancel={props.onCancel}
            title={isEditing ? 'Editar Matriz' : 'Nova Matriz'}
            width={520}
        >
            <InputCP
                label={'Nome'}
                required={true}
                formStateManager={formStateManager}
                fieldName={'name'}
            />

            {
                !isEditing &&
                <SelectCP
                    label={'Pontos'}
                    isMultiple={true}
                    loading={availableRequest.isAwaiting}
                    value={selectedLocationCodes}
                    onChange={setSelectedLocationCodes}
                    options={selectableLocations.map((location) => ({
                        value: location.code,
                        label: location.name,
                    }))}
                />
            }

            {
                isEditing &&
                <>
                    <RowCP>
                        <ColumnCP size={19}>
                            <SelectCP
                                label={'Adicionar pontos'}
                                isMultiple={true}
                                loading={availableRequest.isAwaiting}
                                value={selectedLocationCodes}
                                onChange={setSelectedLocationCodes}
                                options={selectableLocations.map((location) => ({
                                    value: location.code,
                                    label: location.name,
                                }))}
                            />
                        </ColumnCP>
                        <ColumnCP size={5}>
                            <ButtonCP
                                icon={'plus'}
                                type={'primary'}
                                marginTop={23}
                                loading={addRequest.isAwaiting}
                                disabled={!selectedLocationCodes.length}
                                onClick={onClickAddLocations}
                            >
                                {'Adicionar'}
                            </ButtonCP>
                        </ColumnCP>
                    </RowCP>

                    <MembersWrapperSCP>
                        {
                            members.map((member) => (
                                <TagCP
                                    key={member.code}
                                    content={member.name}
                                    color={'blue'}
                                    onClose={() => onClickRemoveLocation(member.code)}
                                />
                            ))
                        }
                        {
                            !detailRequest.isAwaiting && !members.length &&
                            'Nenhum ponto vinculado a esta Matriz.'
                        }
                    </MembersWrapperSCP>
                </>
            }
        </ModalCP>
    )
}

const MembersWrapperSCP = styled.div`
    margin-top: 15px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`
